"use client";
import type { JobPositionEntity } from "@/app/_types/JobPostings";

import { env } from "@/env";
import { getJobLocationDisplayParts } from "@/app/_utilities/jobLocations";
import { Badge } from "@components/core/Badge";
import { Button } from "@components/core/Button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@components/core/Select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@components/core/Tooltip";
import { Link } from "@ui/components/core/Link";
import ChevronRight16 from "@ui/icons/16x16/chevron-right-16.svg";
import { cn } from "@ui/lib/utils";
import { useRouter, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { useState } from "react";

export default function Positions({ positions }: { positions: JobPositionEntity[] }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const t = useTranslations("ClientKeys.Components.Positions");

  const [department, setDepartment] = useState(searchParams.get("department"));
  const [team, setTeam] = useState(searchParams.get("team"));
  const [location, setLocation] = useState(searchParams.get("location"));
  const [departmentsKey, setDepartmentsKey] = useState<string>(`dep-${new Date().getTime()}`);
  const [teamsKey, setTeamsKey] = useState<string>(`team-${new Date().getTime()}`);
  const [locationsKey, setLocationsKey] = useState<string>(`loc-${new Date().getTime()}`);

  // Reusable per-dimension matchers
  const byDept = (p: JobPositionEntity) => !department || p.departmentName === department;
  const byTeam = (p: JobPositionEntity) => !team || p.teamName === team;
  const byLocation = (p: JobPositionEntity) => !location || p.locationNames?.includes(location);

  // Cross-filter: each dimension's options/counts respect all *other* active filters
  // so the dropdowns only show what's reachable given the current selection.
  const departmentCounts = positions
    .filter(p => byTeam(p) && byLocation(p))
    .reduce<Record<string, number>>((acc, p) => {
      acc[p.departmentName] = (acc[p.departmentName] ?? 0) + 1;
      return acc;
    }, {});
  const departments = Object.keys(departmentCounts).sort((a, b) => a.localeCompare(b));

  const teamCounts = positions
    .filter(p => byDept(p) && byLocation(p))
    .reduce<Record<string, number>>((acc, p) => {
      if (p.teamName) acc[p.teamName] = (acc[p.teamName] ?? 0) + 1;
      return acc;
    }, {});
  const teams = Object.keys(teamCounts).sort((a, b) => a.localeCompare(b));

  const locations = positions
    .filter(p => byDept(p) && byTeam(p))
    .flatMap(p => p.locationNames ?? [])
    .filter((loc, index, self) => self.indexOf(loc) === index)
    .sort((a, b) => a.localeCompare(b));

  const filteredPositions = positions.filter(p => byDept(p) && byTeam(p) && byLocation(p));

  // Group filtered positions by department → team.
  // Use "" as the key when a job has no distinct team (blank or same as department)
  // so those jobs are rendered directly under the department heading without a sub-header.
  const groupedFilteredPositions = filteredPositions.reduce<Record<string, Record<string, JobPositionEntity[]>>>(
    (acc, position) => {
      const dept = position.departmentName;
      const teamName = !position.teamName || position.teamName === dept ? "" : position.teamName;
      if (!acc[dept]) acc[dept] = {};
      if (!acc[dept][teamName]) acc[dept][teamName] = [];
      acc[dept][teamName].push(position);
      return acc;
    },
    {},
  );

  const clearFilters = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete("department");
    params.delete("team");
    params.delete("location");
    router.push(`/careers?${params.toString()}`, { scroll: false });
    setDepartment(null);
    setTeam(null);
    setLocation(null);
    setDepartmentsKey(`dep-${new Date().getTime()}`);
    setTeamsKey(`team-${new Date().getTime()}`);
    setLocationsKey(`loc-${new Date().getTime()}`);
  };

  return (
    <div>
      <div className='my-24 flex flex-col gap-12 lg:my-32 lg:flex-row'>
        <Select
          defaultValue={department || ""}
          key={departmentsKey}
          onValueChange={value => {
            const params = new URLSearchParams(window.location.search);
            params.set("department", value);
            params.delete("team");
            router.push(`/careers?${params.toString()}`, { scroll: false });
            setDepartment(value);
            setTeam(null);
            setTeamsKey(`team-${new Date().getTime()}`);
          }}
        >
          <SelectTrigger className='w-full gap-8 xl:w-auto'>
            <SelectValue placeholder={t("all_departments")} />
          </SelectTrigger>
          <SelectContent className='max-h-[350px]'>
            {departments.map(dept => (
              <SelectItem key={dept} value={dept}>
                {dept} ({departmentCounts[dept]})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {teams.length > 0 && (
          <Select
            defaultValue={team || ""}
            key={teamsKey}
            onValueChange={value => {
              const params = new URLSearchParams(window.location.search);
              params.set("team", value);
              router.push(`/careers?${params.toString()}`, { scroll: false });
              setTeam(value);
            }}
          >
            <SelectTrigger className='w-full gap-8 xl:w-auto'>
              <SelectValue placeholder={t("all_teams")} />
            </SelectTrigger>
            <SelectContent className='max-h-[350px]'>
              {teams.map(teamOption => (
                <SelectItem key={teamOption} value={teamOption}>
                  {teamOption} ({teamCounts[teamOption]})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select
          defaultValue={location || ""}
          key={locationsKey}
          onValueChange={value => {
            const params = new URLSearchParams(window.location.search);
            params.set("location", value);
            router.push(`/careers?${params.toString()}`, { scroll: false });
            setLocation(value);
          }}
        >
          <SelectTrigger className='w-full gap-8 xl:w-auto'>
            <SelectValue placeholder={t("all_locations")} />
          </SelectTrigger>
          <SelectContent className='max-h-[350px]'>
            {locations.map(location => (
              <SelectItem key={location} value={location}>
                {location}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button className='w-full xl:w-auto' onClick={clearFilters} variant='elevated'>
          {t("clear_filters")}
        </Button>
      </div>
      <div>
        {Object.entries(groupedFilteredPositions)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([dept, teamGroups]) => (
            <div key={dept} className='mb-48'>
              <h2 className='f-heading-03 mb-32'>{dept}</h2>
              {Object.entries(teamGroups)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([teamName, jobs]) => (
                  <PositionsList
                    baseUrl={env.NEXT_PUBLIC_SERVER_URL + "/careers"}
                    key={teamName || "_direct"}
                    parentLabel={teamName ? dept : undefined}
                    positions={jobs}
                    sectionTitle={teamName || undefined}
                  />
                ))}
            </div>
          ))}
      </div>
    </div>
  );
}

export function PositionsList({
  baseUrl,
  parentLabel,
  positions,
  sectionTitle,
}: {
  baseUrl: string;
  parentLabel?: string;
  positions: JobPositionEntity[];
  sectionTitle?: string;
}) {
  const t = useTranslations("ClientKeys.Components.Positions");
  return (
    <div className='tw-mb-16'>
      {sectionTitle && (
        <div className='tw-mb-4'>
          <h3 className='tw-type-3xl'>
            {parentLabel && <span className='tw-text-black tw-opacity-40'>{parentLabel} / </span>}
            {sectionTitle}
          </h3>
        </div>
      )}
      {positions.map((position, index) => {
        const { shownLocations, remainingLocations } = getJobLocationDisplayParts(position, t("remote"), 2);
        return (
          <Link
            className={cn(
              "group flex flex-col items-start justify-between gap-8 border-b border-default py-12 lg:flex-row lg:items-center",
              index === 0 && "border-t border-t-default",
            )}
            href={`${baseUrl}/${position.slug}`}
            key={position.id}
            variant='clean'
          >
            <p>{position.title}</p>
            <div className='flex items-center gap-8'>
              {shownLocations.map((location, index) => (
                <Badge key={index}>{location}</Badge>
              ))}
              {remainingLocations.length > 0 && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger className='flex items-center'>
                      <Badge>{t("more_locations", { count: remainingLocations.length })}</Badge>
                    </TooltipTrigger>
                    <TooltipContent className='shadow-none f-description-04 bg-inverse p-4 px-10 text-inverse'>
                      {remainingLocations.join(", ")}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              <div className='hidden w-0 overflow-hidden transition-all duration-200 group-hover:w-16 xl:block'>
                <ChevronRight16 className='scale-0 transition-all duration-200 group-hover:scale-100' />
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
}
